"use client";

import type { ReactNode } from "react";
import { ArrowUp, Mic, User } from "lucide-react";
import { Button } from "@/shadcn-components/ui/button";
import { ScrollArea } from "@/shadcn-components/ui/scroll-area";
import { VybeAgentMark } from "@/components/desktop/agent/VybeAgentMark";
import { cn } from "@/lib/utils";

const SUGGESTIONS = [
  "Explain my [Input] fields",
  "Add a second plot buffer",
  "Fix OnCalculate warmup",
] as const;

function MockBubble({
  role,
  children,
}: {
  role: "agent" | "user";
  children: ReactNode;
}) {
  const isAgent = role === "agent";
  return (
    <div className="flex min-w-0 gap-2.5">
      <div className="mt-0.5 flex shrink-0 items-start">
        {isAgent ? (
          <VybeAgentMark size={14} tone="primary" />
        ) : (
          <User className="h-3.5 w-3.5 text-muted-foreground" />
        )}
      </div>
      <div className="min-w-0 flex-1">
        <div className="mb-1 text-[11px] font-medium text-muted-foreground">
          {isAgent ? "Vybe Agent" : "You"}
        </div>
        <div className="text-[11px] leading-relaxed text-foreground/90">
          {children}
        </div>
      </div>
    </div>
  );
}

export default function NavigatorAgentSection() {
  return (
    <div className="flex h-full min-h-0 flex-col">
      <div className="shrink-0 border-b border-border px-2.5 py-2">
        <div className="flex items-center gap-2">
          <VybeAgentMark size={16} tone="primary" />
          <div className="min-w-0">
            <p className="text-[11px] font-medium text-foreground">Vybe Agent</p>
            <p className="text-[10px] text-muted-foreground">
              Indicator assistant (mockup)
            </p>
          </div>
        </div>
      </div>

      <ScrollArea className="min-h-0 flex-1">
        <div className="space-y-4 p-2.5">
          <MockBubble role="user">
            How do I add a color input for my main plot line?
          </MockBubble>
          <MockBubble role="agent">
            Use an <code className="text-[10px]">[Input]</code> with type color
            in your indicator class, then reference it in the{" "}
            <code className="text-[10px]">[Plot]</code> attribute. I can draft
            the attribute syntax when connected.
          </MockBubble>
          <MockBubble role="user">
            What should SetMinBars be for a 14-period EMA?
          </MockBubble>
          <MockBubble role="agent">
            Set <code className="text-[10px]">SetMinBars(Period)</code> in{" "}
            <code className="text-[10px]">OnInit</code> so the first valid bar
            aligns with your warmup logic in OnCalculate.
          </MockBubble>

          <div className="flex flex-wrap gap-1.5 pt-1">
            {SUGGESTIONS.map((label) => (
              <button
                key={label}
                type="button"
                disabled
                className={cn(
                  "rounded-md border border-border bg-muted/40 px-2 py-1 text-[10px] text-muted-foreground",
                )}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </ScrollArea>

      <div className="shrink-0 space-y-1.5 border-t border-border p-2.5">
        <div className="rounded-md border border-border bg-muted/30">
          <textarea
            disabled
            placeholder="Ask about this indicator…"
            className="min-h-[52px] w-full resize-none bg-transparent px-2.5 py-2 text-[11px] outline-none placeholder:text-muted-foreground disabled:cursor-not-allowed disabled:opacity-70"
          />
          <div className="flex items-center justify-end gap-1 border-t border-border/60 px-1.5 py-1">
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled
              aria-hidden
            >
              <Mic className="h-3.5 w-3.5" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="h-7 w-7"
              disabled
              aria-hidden
            >
              <ArrowUp className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>
        <p className="text-center text-[10px] text-muted-foreground">
          Mockup — not connected
        </p>
      </div>
    </div>
  );
}
